import React, { useRef, useEffect, useMemo } from 'react';
import Chart from 'chart.js/auto';

const TransactionsPieChart = ({ transactions, negateForExpenses = false, includePayments = false, excludedCategories = [], budgetCategoryAmounts = null, budgetMonthlyTotal = null, enforceExpenseSign = false, excludeTransfers = true, categoryHierarchy = [] }) => {
    const [showSubcategories, setShowSubcategories] = React.useState(false);
    const canvasRef = useRef(null);
    const chartRef = useRef(null);

    // Base categories to exclude by default; for credit cards we exclude payments and rewards
    const baseExcluded = ['rewards_cashback'];
    if (!includePayments) {
        baseExcluded.push('payments');
    }
    if (excludeTransfers) {
        baseExcluded.push('transfer');
    }
    // Merge with any additional exclusions provided by caller
    const effectiveExcluded = Array.from(new Set([...(excludedCategories || []), ...baseExcluded]));

    // Get category label from value - moved before useMemo
    const getCategoryLabel = (categoryValue) => {
        // Build label map from categoryHierarchy
        const labelMap = new Map();
        if (Array.isArray(categoryHierarchy)) {
            categoryHierarchy.forEach(parent => {
                labelMap.set(parent.value, parent.label);
                if (parent.children && Array.isArray(parent.children)) {
                    parent.children.forEach(child => {
                        labelMap.set(child.value, child.label);
                    });
                }
            });
        }

        const toTitle = (slug) => {
            if (!slug) return '';
            // Remove leading underscores
            let s = slug.replace(/^_+/, '');
            // Replace remaining underscores with spaces
            s = s.replace(/_+/g, ' ');
            // Convert to Title Case
            return s.split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
        };

        // Support composite keys like "parent|child" to render "Parent - Child"
        if (typeof categoryValue === 'string' && categoryValue.includes('|')) {
            const [p, c] = categoryValue.split('|');
            let pl = getCategoryLabel(p);
            let cl = getCategoryLabel(c);

            // Clean up duplications like "Media - Media (Other)" -> "Media - Other"
            if (cl.toLowerCase().startsWith(pl.toLowerCase())) {
                const remaining = cl.slice(pl.length).trim();
                // Remove leading dashes or spaces
                cl = remaining.replace(/^[:\-\s]+/, '');
                // Ensure first letter is capitalized
                cl = cl.charAt(0).toUpperCase() + cl.slice(1);
            }
            return `${pl} - ${cl}`;
        }
        const categoryMap = {
            'transfer': 'Transfer',
            'payments': 'Payments',
            'rewards_cashback': 'Rewards/Cashback',
            'income_nonrecurring': 'Income (Non-recurring)',
            'income_recurring': 'Income (Recurring)',
            'other': 'Other',
            'none': 'Uncategorized',
        };
        return labelMap.get(categoryValue) || categoryMap[categoryValue] || toTitle(categoryValue);
    };

    // Process transactions data for the chart
    const chartData = useMemo(() => {
        if (!transactions || transactions.length === 0) {
            return { labels: [], data: [], backgroundColor: [], totalAmount: 0, sortedEntries: [], categoryColors: {}, categoryTotals: {} };
        }

        // Filter out excluded categories and group by expense category
        const categoryTotals = {};
        let totalAmount = 0;

        // Build child-to-parent mapping from categoryHierarchy
        const childToParentMap = new Map();
        if (Array.isArray(categoryHierarchy)) {
            categoryHierarchy.forEach(parent => {
                if (parent.children && Array.isArray(parent.children)) {
                    parent.children.forEach(child => {
                        childToParentMap.set(child.value, parent.value);
                    });
                }
            });
        }

        const childToParent = (val) => {
            if (childToParentMap.has(val)) return childToParentMap.get(val);
            // Legacy fallbacks if hierarchy is missing
            const HOME_CHILDREN = new Set(['home_repair','home_maintenance','home_enhancement','home_decor','home_furniture','home_artwork','home_pest_control','home_other','home_improvement','home_inspection','home_tax_payment','home_insurance_payment', 'home_plants']);
            const UTILITIES_CHILDREN = new Set(['water','gas','electric','electricity','electric_gas','utilities__other']);
            const MEDIA_CHILDREN = new Set(['apps','internet','tv','books','cellphones','media__other','media_other','tv_cellphones','media_entertainment']);
            const PERSONAL_CARE_CHILDREN = new Set(['gym_memberships_fees','supplements','hair_care','nails','personal_care__other','skin_care_products','physical_therapy','personal_care_other','prescriptions','medicines','make_up','skin_care_treatments','spa_massage_sauna','dental_products', 'workout_gym_products']);
            const WASTE_CHILDREN = new Set(['sanitation','waste_management__recycling','waste_management__yard_waste','waste_management__other']);
            const KIDS_CHILDREN = new Set(['kids_clothing','kids_sports_league_fees','kids_sports_equipment_clothing','kids_dance_fees','kids_dance_clothing','kids_school_meals','kids_shopping','kids_camp_tuition_fees','kids__other','kids_other','kids_bday_parties','kids_activities']);
            const PAYMENTS_CHILDREN = new Set(['financing_interest','financing_principal']);

            if (HOME_CHILDREN.has(val)) return 'home';
            if (UTILITIES_CHILDREN.has(val)) return 'utilities';
            if (MEDIA_CHILDREN.has(val)) return 'media';
            if (PERSONAL_CARE_CHILDREN.has(val)) return 'personal_care';
            if (WASTE_CHILDREN.has(val)) return 'waste_management';
            if (KIDS_CHILDREN.has(val)) return 'kids';
            if (PAYMENTS_CHILDREN.has(val)) return 'payments';
            return null;
        };

        transactions.forEach(transaction => {
            // Enforce expense sign semantics when requested (for cash accounts, only negative amounts are expenses)
            let original = parseFloat(transaction.amount);
            if (isNaN(original)) original = 0;
            if (enforceExpenseSign) {
                if (negateForExpenses) {
                    if (!(original < 0)) return; // skip deposits/refunds
                } else {
                    if (!(original > 0)) return; // skip credits/refunds
                }
            }
            const rawCat = transaction.expense_category || 'none';
            let amount = parseFloat(transaction.amount);
            if (isNaN(amount)) amount = 0;
            if (negateForExpenses) amount = -amount; // expenses as positive magnitudes for cash accounts

            // Skip excluded categories based on the raw category
            if (effectiveExcluded.includes(rawCat)) {
                return;
            }

            // Drop debt_payment children from the pie chart —
            // the parent "debt_payment" transaction already carries the full amount.
            const DEBT_PAYMENT_CHILDREN = new Set(['principal_payment', 'interest_payment', 'insurance_payment', 'tax_payment']);
            if (DEBT_PAYMENT_CHILDREN.has(rawCat)) {
                return;
            }

            // Determine final bucket key depending on toggle
            let category = rawCat;
            const parent = childToParent(rawCat);
            if (!showSubcategories) {
                // Aggregate to parent category when applicable (also map legacy home_improvement)
                if (rawCat === 'home_improvement') category = 'home';
                else if (parent) category = parent;
            } else {
                // Show sub-categories: use composite key parent|child when a parent exists
                if (rawCat === 'home_improvement') {
                    category = 'home|home_improvement';
                } else if (parent && parent !== rawCat) {
                    category = `${parent}|${rawCat}`;
                }
            }

            if (!categoryTotals[category]) {
                categoryTotals[category] = 0;
            }
            categoryTotals[category] += Math.abs(amount); // Ensure expenses are shown as positive magnitudes
        });

        // Calculate total of all positive category net amounts for percentage calculation
        totalAmount = Object.values(categoryTotals)
            .filter(amount => amount > 0) // Only include positive net amounts in total
            .reduce((sum, amount) => sum + amount, 0);

        // Convert to arrays for Chart.js - only include categories with positive net amounts
        const labels = [];
        const data = [];
        const backgroundColor = [];

        // Color palette for categories
        const categoryColors = {
            'food_dining': '#FF6B35',
            'gas_fuel': '#7B1FA2',
            'groceries': '#2E7D32',
            'internet': '#1565C0',
            'apps': '#5E35B1',
            'books': '#fbbf24',
            'landscaping': '#388E3C',
            'shopping': '#C2185B',
            'entertainment': '#0277BD',
            'travel': '#F57C00',
            'automotive': '#00695C',
            'healthcare': '#C62828',
            'utilities': '#EF6C00',
            'subscription': '#8E24AA',
            'insurance': '#3F51B5',
            'taxes': '#B71C1C',
            'kids': '#22c55e',
            'education': '#0097A7',
            'business': '#455A64',
            'personal_care': '#AD1457',
            'home': '#8b5cf6',
                        'home_improvement': '#558B2F',
            'gifts_charity': '#9E9D24',
            'fees_charges': '#D32F2F',
            'transfer': '#1976D2',
            'payments': '#2E7D32',
            'rewards_cashback': '#F57C00',
            'waste_management': '#10b981',
            'sanitation': '#10b981',
            'waste_management__recycling': '#34d399',
            'waste_management__yard_waste': '#059669',
            'waste_management__other': '#6ee7b7',
            'utilities__other': '#93c5fd',
            'tv_cellphones': '#f472b6',
                        'tv': '#7c3aed',
                        'cellphones': '#f472b6',
                        'media': '#2563eb',
            'gym_memberships_fees': '#84cc16',
            'supplements': '#a3e635',
            'hair_care': '#f9a8d4',
            'nails': '#fb7185',
            'personal_care__other': '#fecaca',
            'dental_products': '#ffe4e6',
            'workout_gym_products': '#bef264',
            'other': '#616161',
        };

        // Sort by amount (largest first) and only include positive net amounts
        const sortedEntries = Object.entries(categoryTotals)
            .filter(([category, amount]) => amount > 0) // Only show categories with net positive expenses
            .sort((a, b) => b[1] - a[1]);

        sortedEntries.forEach(([category, amount]) => {
            const categoryLabel = getCategoryLabel(category);
            labels.push(categoryLabel);

            // Always show percentage in the chart data
            const pct = totalAmount > 0 ? (amount / totalAmount) * 100 : 0;
            data.push(Number(pct.toFixed(1)));

            // Use parent color for composite keys parent|child
            let colorKey = category;
            if (typeof category === 'string' && category.includes('|')) {
                colorKey = category.split('|')[0];
            }
            backgroundColor.push(categoryColors[colorKey] || '#616161');
        });

        return { labels, data, backgroundColor, totalAmount, sortedEntries, categoryColors, categoryTotals };
    }, [transactions, getCategoryLabel, showSubcategories, effectiveExcluded, negateForExpenses, enforceExpenseSign, categoryHierarchy]);

    // Prepare outlabel entries for Income Statement (budget overlay). Only when budget props are provided.
    const overlayEntries = useMemo(() => {
        try {
            if (!budgetCategoryAmounts || !budgetMonthlyTotal) return [];
            const totalBudget = Number(budgetMonthlyTotal) || 0;
            if (totalBudget <= 0) return [];
            const sorted = Array.isArray(chartData.sortedEntries) ? chartData.sortedEntries : [];
            if (!sorted.length) return [];
            return sorted
                .map(([category, actualAmt]) => {
                    const budgetAmtRaw = budgetCategoryAmounts?.[category];
                    const budgetAmt = budgetAmtRaw != null ? Number(budgetAmtRaw) : 0;
                    const label = getCategoryLabel(category);
                    const actual = Number(actualAmt) || 0;
                    const budgetPct = totalBudget > 0 ? (budgetAmt / totalBudget) * 100 : 0;
                    const actualPctRaw = chartData.totalAmount > 0 ? (actual / chartData.totalAmount) * 100 : 0;
                    const actualPct = Number(actualPctRaw.toFixed(1));
                    // Hide labels for categories under 4.0% of total actual expenses
                    if (actualPct < 4.0) return null;
                    const diffAmt = budgetAmt - actual;
                    const diffPct = budgetPct - actualPct;
                    return {
                        label,
                        budgetAmt,
                        budgetPct,
                        actualAmt: actual,
                        actualPct,
                        diffAmt,
                        diffPct
                    };
                })
                .filter(Boolean);
        } catch (e) {
            return [];
        }
    }, [budgetCategoryAmounts, budgetMonthlyTotal, chartData.sortedEntries, chartData.totalAmount, getCategoryLabel]);

    // Create/update chart
    useEffect(() => {
        if (!chartData.labels.length) {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
            return;
        }

        // Clear previous chart
        if (chartRef.current) {
            chartRef.current.destroy();
        }

        // Create new chart
        const overlayPlugin = {
            id: 'budgetOverlayLabels',
            afterDatasetsDraw(chart, args, pluginOptions) {
                const entries = (pluginOptions && Array.isArray(pluginOptions.entries)) ? pluginOptions.entries : (chart.$overlayEntries || []);
                if (!entries.length) return;
                const ctx = chart.ctx;
                const meta = chart.getDatasetMeta(0);
                if (!meta || !meta.data) return;
                const centerX = chart.chartArea.left + (chart.chartArea.right - chart.chartArea.left) / 2;
                const centerY = chart.chartArea.top + (chart.chartArea.bottom - chart.chartArea.top) / 2;
                ctx.save();
                ctx.font = '11px system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif';
                ctx.textBaseline = 'middle';

                // Prepare right-aligned numeric strings per entry lines
                entries.forEach((entry, i) => {
                    const arc = meta.data[i];
                    if (!arc) return;
                    // Compute mid-angle and outer radius to ensure label is outside the pie perimeter
                    const props = arc.getProps ? arc.getProps(['startAngle','endAngle','outerRadius','x','y'], true) : {};
                    const startA = props.startAngle != null ? props.startAngle : 0;
                    const endA = props.endAngle != null ? props.endAngle : 0;
                    const midA = (startA + endA) / 2 || 0;
                    const cx = props.x != null ? props.x : centerX;
                    const cy = props.y != null ? props.y : centerY;
                    const outerR = props.outerRadius != null ? props.outerRadius : Math.hypot(arc.x - cx, arc.y - cy);

                    // Edge point on arc perimeter
                    const xEdge = cx + Math.cos(midA) * outerR;
                    const yEdge = cy + Math.sin(midA) * outerR;

                    // Place the label slightly outside the perimeter along the mid-angle
                    const labelR = outerR + 22;
                    const xLabelRadial = cx + Math.cos(midA) * labelR;
                    const yLabelRadial = cy + Math.sin(midA) * labelR;

                    const alignRight = Math.cos(midA) >= 0;
                    const labelX = xLabelRadial + (alignRight ? 8 : -8);
                    const labelY = yLabelRadial;
                    // leader line
                    ctx.strokeStyle = '#9ca3af';
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.moveTo(xEdge, yEdge);
                    ctx.lineTo(xLabelRadial, yLabelRadial);
                    ctx.stroke();
                    // text lines with aligned columns (monospace)
                    const textColor = '#e5e7eb';
                    const green = '#16a34a';
                    const red = '#ef4444';
                    ctx.fillStyle = textColor;
                    ctx.textAlign = alignRight ? 'left' : 'right';

                    // Build left labels (with colon) and right numeric strings
                    const left1 = `${entry.label}:`;
                    const left2 = 'Actual:';
                    const left3 = 'Diff:';
                    const leftWidth = Math.max(left1.length, left2.length, left3.length);

                    const num1 = `$${Math.round(entry.budgetAmt).toLocaleString()} (${entry.budgetPct.toFixed(1)}%)`;
                    const num2 = `$${Math.round(entry.actualAmt).toLocaleString()} (${entry.actualPct.toFixed(1)}%)`;
                    const diffSign = entry.diffAmt >= 0 ? '' : '-';
                    const num3 = `${diffSign}$${Math.round(Math.abs(entry.diffAmt)).toLocaleString()} (${(entry.diffPct >= 0 ? '' : '-')}${Math.abs(entry.diffPct).toFixed(1)}%)`;
                    const numWidth = Math.max(num1.length, num2.length, num3.length);

                    const line1 = `${left1.padEnd(leftWidth)} ${num1.padStart(numWidth)}`;
                    const line2 = `${left2.padEnd(leftWidth)} ${num2.padStart(numWidth)}`;
                    const line3 = `${left3.padEnd(leftWidth)} ${num3.padStart(numWidth)}`;

                    // Draw lines
                    ctx.fillStyle = textColor;
                    ctx.fillText(line1, labelX, labelY - 10);
                    ctx.fillText(line2, labelX, labelY + 4);
                    ctx.fillStyle = entry.diffAmt >= 0 ? green : red;
                    ctx.fillText(line3, labelX, labelY + 18);
                });
                ctx.restore();
            }
        };

        chartRef.current = new Chart(canvasRef.current, {
            type: 'pie',
            data: {
                labels: chartData.labels,
                datasets: [{
                    data: chartData.data,
                    backgroundColor: chartData.backgroundColor,
                    borderWidth: 2,
                    borderColor: '#ffffff',
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                layout: { padding: (Array.isArray(overlayEntries) && overlayEntries.length > 0) ? { top: 24, right: 72, bottom: 72, left: 72 } : 24 },
                plugins: {
                    title: {
                        display: true,
                        text: 'Net Expense Categories (%)',
                        font: { size: 16, weight: 'bold' },
                        color: '#e5e7eb'
                    },
                    legend: {
                        position: 'right',
                        labels: {
                            color: '#e5e7eb',
                            boxWidth: 12,
                            padding: 10,
                            font: { size: 11 }
                        }
                    },
                    tooltip: {
                        backgroundColor: '#1f2937',
                        titleColor: '#e5e7eb',
                        bodyColor: '#e5e7eb',
                        displayColors: false,
                        bodyFont: { family: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif', size: 12 },
                        titleFont: { family: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif', size: 12 },
                        callbacks: {
                            label: function(context) {
                                const idx = context.dataIndex;
                                const percent = Number(context.parsed);
                                const chart = chartRef.current;
                                // Actual amount from sortedEntries
                                const actualAmt = (chart && chart.$sortedEntries)
                                    ? (Number(chart.$sortedEntries[idx]?.[1]) || 0)
                                    : (Array.isArray(chartData.sortedEntries) ? (Number(chartData.sortedEntries[idx]?.[1]) || 0) : 0);

                                // Try to use prepared overlay entry first
                                const entry = (chart && Array.isArray(chart.$overlayEntries)) ? chart.$overlayEntries[idx] : null;

                                // If no prepared entry, compute budget values directly (when possible)
                                let budgetAmt = 0, budgetPct = 0, actualPct = percent, diffAmt = 0, diffPct = 0;
                                if (entry) {
                                    budgetAmt = Number(entry.budgetAmt) || 0;
                                    budgetPct = Number(entry.budgetPct) || 0;
                                    // prefer entry.actualPct if present (already rounded in data prep)
                                    actualPct = (typeof entry.actualPct === 'number') ? entry.actualPct : percent;
                                    diffAmt = Number(entry.diffAmt) || 0;
                                    diffPct = (typeof entry.diffPct === 'number') ? entry.diffPct : (budgetPct - actualPct);
                                } else if (budgetCategoryAmounts && budgetMonthlyTotal) {
                                    const catKey = (chart && chart.$sortedEntries && chart.$sortedEntries[idx]) ? chart.$sortedEntries[idx][0] : (Array.isArray(chartData.sortedEntries) ? chartData.sortedEntries[idx]?.[0] : null);
                                    const totalBudget = Number(budgetMonthlyTotal) || 0;
                                    const bAmt = catKey ? Number(budgetCategoryAmounts[catKey]) || 0 : 0;
                                    budgetAmt = bAmt;
                                    budgetPct = totalBudget > 0 ? (bAmt / totalBudget) * 100 : 0;
                                    diffAmt = budgetAmt - actualAmt;
                                    diffPct = budgetPct - actualPct;
                                } else {
                                    // No budget data at all; fall back to previous single-line label
                                    const label = context.label || '';
                                    const amtStr = `$${Number(actualAmt).toLocaleString()} (${percent.toFixed(1)}%)`;
                                    const left = 'Category';
                                    const leftCol = (left + ':').padEnd(8);
                                    return `${leftCol} ${amtStr}`;
                                }

                                // Build aligned strings
                                const numBudget = `$${Math.round(budgetAmt).toLocaleString()} (${budgetPct.toFixed(1)}%)`;
                                const numActual = `$${Math.round(actualAmt).toLocaleString()} (${actualPct.toFixed(1)}%)`;
                                const diffSign = diffAmt >= 0 ? '' : '-';
                                const diffAbs = Math.abs(diffAmt);
                                const numDiff = `${diffSign}$${Math.round(diffAbs).toLocaleString()} (${(diffPct >= 0 ? '' : '-')}${Math.abs(diffPct).toFixed(1)}%)`;
                                const maxLen = Math.max(numBudget.length, numActual.length, numDiff.length);
                                const leftBudget = 'Budget:';
                                const leftActual = 'Actual:';
                                const leftDiff = 'Diff:';
                                const leftWidth = 7; // accommodates 'Actual:'

                                // Return only the first line here; afterLabel will return the remaining lines
                                return `${leftBudget.padEnd(leftWidth)} ${numBudget.padStart(maxLen)}`;
                            },
                            afterLabel: function(context) {
                                const idx = context.dataIndex;
                                const percent = Number(context.parsed);
                                const chart = chartRef.current;
                                const actualAmt = (chart && chart.$sortedEntries)
                                    ? (Number(chart.$sortedEntries[idx]?.[1]) || 0)
                                    : (Array.isArray(chartData.sortedEntries) ? (Number(chartData.sortedEntries[idx]?.[1]) || 0) : 0);

                                const entry = (chart && Array.isArray(chart.$overlayEntries)) ? chart.$overlayEntries[idx] : null;
                                let actualPct = percent;
                                let budgetPct = 0;
                                let diffAmt = 0;
                                let diffPct = 0;
                                let budgetAmt = 0;

                                if (entry) {
                                    actualPct = (typeof entry.actualPct === 'number') ? entry.actualPct : percent;
                                    budgetPct = Number(entry.budgetPct) || 0;
                                    diffAmt = Number(entry.diffAmt) || 0;
                                    diffPct = (typeof entry.diffPct === 'number') ? entry.diffPct : (budgetPct - actualPct);
                                    budgetAmt = Number(entry.budgetAmt) || 0;
                                } else if (budgetCategoryAmounts && budgetMonthlyTotal) {
                                    const catKey = (chart && chart.$sortedEntries && chart.$sortedEntries[idx]) ? chart.$sortedEntries[idx][0] : (Array.isArray(chartData.sortedEntries) ? chartData.sortedEntries[idx]?.[0] : null);
                                    const totalBudget = Number(budgetMonthlyTotal) || 0;
                                    const bAmt = catKey ? Number(budgetCategoryAmounts[catKey]) || 0 : 0;
                                    budgetAmt = bAmt;
                                    budgetPct = totalBudget > 0 ? (bAmt / totalBudget) * 100 : 0;
                                    diffAmt = bAmt - actualAmt;
                                    diffPct = budgetPct - actualPct;
                                } else {
                                    // No budget data; no extra lines
                                    return [];
                                }

                                const numBudget = `$${Math.round(budgetAmt).toLocaleString()} (${budgetPct.toFixed(1)}%)`;
                                const numActual = `$${Math.round(actualAmt).toLocaleString()} (${actualPct.toFixed(1)}%)`;
                                const diffSign = diffAmt >= 0 ? '' : '-';
                                const diffAbs = Math.abs(diffAmt);
                                const numDiff = `${diffSign}$${Math.round(diffAbs).toLocaleString()} (${(diffPct >= 0 ? '' : '-')}${Math.abs(diffPct).toFixed(1)}%)`;
                                const maxLen = Math.max(numBudget.length, numActual.length, numDiff.length);
                                const leftWidth = 7; // accommodates 'Actual:'
                                const lineActual = `${'Actual:'.padEnd(leftWidth)} ${numActual.padStart(maxLen)}`;
                                const lineDiff = `${'Diff:'.padEnd(leftWidth)} ${numDiff.padStart(maxLen)}`;
                                return [lineActual, lineDiff];
                            }
                        }
                    }
                },
                budgetOverlayLabels: {
                    entries: overlayEntries
                }
            },
            plugins: (Array.isArray(overlayEntries) && overlayEntries.length > 0) ? [overlayPlugin] : []
        });
        // Expose sorted entries and overlay entries for tooltip/back-compat
        if (chartRef.current) {
            chartRef.current.$sortedEntries = chartData.sortedEntries;
            chartRef.current.$overlayEntries = overlayEntries;
        }
    }, [chartData]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
            }
        };
    }, []);

    if (!transactions || transactions.length === 0) {
        return (
            <div className="text-center py-4">
                <p className="text-muted">No transaction data available for chart.</p>
            </div>
        );
    }

    if (!chartData.labels.length) {
        return (
            <div className="text-center py-4">
                <p className="text-muted">No net positive expenses to display in chart.</p>
                <small className="text-muted">
                    Only categories with net positive expenses are shown. Payments, Rewards/Cashback, and Uncategorized transactions are excluded.
                </small>
            </div>
        );
    }

    return (
        <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>Net Expense Breakdown</h4>
                <div className="form-check form-switch" style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <input className="form-check-input" type="checkbox" id="showSubsSwitch" checked={showSubcategories} onChange={(e)=> setShowSubcategories(e.target.checked)} />
                    <label className="form-check-label" htmlFor="showSubsSwitch">Show sub-categories</label>
                </div>
            </div>

            <div className="chart-card" style={{ height: '460px', position: 'relative' }}>
                <canvas ref={canvasRef}></canvas>
            </div>

        </div>
    );
};

export default TransactionsPieChart;